import { Button, Space, type TableProps } from 'antd';
import { useGetAttendanceReportsQuery } from '../../../../../app/features/employee/emp-attendance/emp-attendance.api';
import CustomTable from '../../../../../components/CustomTable';
import PageTitle from '../../../../../components/PageTitle';
import StatusTag from '../../../../../components/StatusTag';
import useError from '../../../../../hooks/useError';
import useFilters from '../../../../../hooks/useFilters';
import DashboardLayout from '../../../../../layouts/DashboardLayout';
import { countParPageSerialNumber } from '../../../../../utils/global.utils';

import { formatDate } from '@/utils';

const REPORT_STATUS_CONFIG = [
  {
    label: 'Completed',
    value: 'COMPLETED',
    className: 'status status-success',
  },
  { label: 'Failed', value: 'FAILED', className: 'status status-error' },
  {
    label: 'Processing',
    value: 'PROCESSING',
    className: 'status status-yellow',
  },
];

export default function Report() {
  // Use filters hook
  const { page, limit, handlePagination } = useFilters();

  // ------- Query API ---------
  const { data, isLoading, error } = useGetAttendanceReportsQuery({
    params: {
      page,
      limit,
    },
  });

  // ------ Error Handeling ---------

  useError(error, 'Report list failed');

  const columns: TableProps<any>['columns'] = [
    {
      title: 'SL',
      align: 'center',
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: 'Request For',
      dataIndex: 'reportName',
      render: (val: string) => val || 'N/A',
    },

    {
      title: 'Report Range Date',
      align: 'center',
      width: 200,
      render: (record) => {
        const { startDate, endDate } = record;
        const start = startDate ? formatDate(startDate, 'DD MMM, YYYY') : null;
        const end = endDate ? formatDate(endDate, 'DD MMM YYYY') : null;
        if (!start && !end) return <span>--</span>;
        if (start && end) {
          return <span>{`${start} - ${end}`}</span>;
        }

        return <span>{start || end}</span>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      width: 120,
      render: (val: string) =>
        val ? (
          <span className="text-nowrap">
            {formatDate(val, 'DD MMM, YYYY - hh:mm A')}
          </span>
        ) : (
          '--'
        ),
    },
    {
      title: 'Remarks',
      dataIndex: 'failedError',
      render: (val) => val ?? '--',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={REPORT_STATUS_CONFIG} />
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'fileUrl',
      align: 'center',
      render: (val: string) => {
        const disabled = !val;

        return (
          <Space>
            <Button
              size="small"
              type="default"
              disabled={disabled}
              className={`
      text-[#00C6AE] border-none !bg-transparent shadow-none
      underline underline-offset-4
      ${disabled ? 'decoration-gray-400 text-gray-400' : 'decoration-[#00C6AE]'}
    `}
              onClick={() => {
                if (!val) return;

                // Create a temporary link to download
                const link = document.createElement('a');
                link.href = val;
                // Use the filename from URL or fallback
                link.download = val.split('/').pop() || 'report.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <DashboardLayout title="Report">
      <PageTitle
        title="Report"
        items={[
          { title: 'Dashboard', path: '/admin/employee/dashboard' },
          { title: 'Reports' },
        ]}
      ></PageTitle>

      <div className="mt-6">
        <CustomTable
          page={page}
          limit={limit}
          hideRowSelect
          columns={columns}
          dataSource={data?.data?.data ?? []}
          onPaginate={handlePagination}
          customTitle="Reports"
          loading={isLoading}
          total={data?.data?.meta?.total}
        />
      </div>
    </DashboardLayout>
  );
}
